import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  SafeAreaView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { listUsers } from '../services/database';
import { toCamelCase } from '../services/AuthContext';
import Icon from 'react-native-vector-icons/MaterialIcons';

const UnclaimedClubsScreen = ({ navigation }) => {
  useAuth(); // Auth context available if needed
  const { showToast } = useToast();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    fetchUnclaimedClubs();
  }, []);

  const fetchUnclaimedClubs = async () => {
    try {
      setLoading(true);
      
      // Fetch all club users from Supabase
      const allClubs = await listUsers({ accountType: 'CLUB' });
      
      // Filter unclaimed clubs (where claimed_by is null or undefined)
      // Convert to camelCase for component compatibility
      const unclaimed = allClubs
        .filter(club => !club.claimed_by)
        .map(club => toCamelCase(club));

      setClubs(unclaimed || []);
    } catch (error) {
      console.error('Error fetching unclaimed clubs:', error);
      showToast('Failed to load unclaimed clubs', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchUnclaimedClubs();
  };

  const renderClubCard = ({ item }) => (
    <TouchableOpacity
      style={styles.clubCard}
      onPress={() => navigation.navigate('UserDetail', { userId: item.id })}
      activeOpacity={0.7}
    >
      <View style={styles.clubCardContent}>
        <View style={styles.clubHeader}>
          <Text style={styles.clubName}>{item.clubName || item.username}</Text>
          <View style={styles.unclaimedBadge}>
            <Text style={styles.unclaimedBadgeText}>Unclaimed</Text>
          </View>
        </View>
        
        {item.address && (
          <View style={styles.clubDetail}>
            <Icon name="location-on" size={16} color="#666" />
            <Text style={styles.clubDetailText} numberOfLines={1}>{item.address}</Text>
          </View>
        )}
        
        {item.email && (
          <View style={styles.clubDetail}>
            <Icon name="email" size={16} color="#666" />
            <Text style={styles.clubDetailText} numberOfLines={1}>{item.email}</Text>
          </View>
        )}

        {item.about && (
          <Text style={styles.clubAbout} numberOfLines={2}>{item.about}</Text>
        )}
      </View>

      <Icon name="chevron-right" size={24} color="#999" />
    </TouchableOpacity>
  );

  if (loading && !refreshing) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backIcon}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Unclaimed Clubs</Text>
        <View style={{ width: 24 }} />
      </View>

      <FlatList
        data={clubs}
        keyExtractor={(item) => item.id}
        renderItem={renderClubCard}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#FF9800" />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Icon name="check-circle" size={64} color="#94EB98" />
            <Text style={styles.emptyText}>All clubs are claimed!</Text>
            <Text style={styles.emptySubtext}>Great job managing the community.</Text>
          </View>
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backIcon: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  listContent: {
    padding: 12,
  },
  clubCard: {
    flexDirection: 'row',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  clubCardContent: {
    flex: 1,
    marginRight: 12,
  },
  clubHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  clubName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  unclaimedBadge: {
    backgroundColor: '#FFE0B2',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 8,
  },
  unclaimedBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#FF9800',
  },
  clubDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    gap: 6,
  },
  clubDetailText: {
    fontSize: 14,
    color: '#666',
    flex: 1,
  },
  clubAbout: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    lineHeight: 20,
  },
  claimButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FF9800',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    minWidth: 90,
  },
  claimButtonDisabled: {
    opacity: 0.6,
  },
  claimButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center',
  },
});

export default UnclaimedClubsScreen;

